// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.

/**
 * Turns thrown API errors into copy a user can act on.
 *
 * `ApiError.message` carries whatever the server said, which ranges from a
 * clean sentence to a raw JSON envelope from a downstream service. Neither is
 * something to render verbatim in a page-level error state, so map the HTTP
 * status to our own wording and only fall back to the server text when the
 * status tells us nothing.
 */

import { ApiError } from '../services/api';

export interface FriendlyError {
  /** Short headline for the error state. */
  title: string;
  /** One sentence explaining what happened and what to do next. */
  message: string;
  /** True when retrying could plausibly succeed (drives a "Try again" button). */
  retryable: boolean;
}

interface DescribeOptions {
  /**
   * What the user was trying to do, phrased to slot into "permission to _".
   * e.g. "view users", "invite this user".
   */
  action?: string;
}

/** Server text that is really a machine payload — never show it to a user. */
function isPresentable(message: string | undefined): message is string {
  if (!message) return false;
  const trimmed = message.trim();
  if (!trimmed || trimmed.startsWith('{') || trimmed.startsWith('[')) return false;
  return true;
}

export function describeApiError(error: unknown, options: DescribeOptions = {}): FriendlyError {
  const action = options.action ?? 'do this';
  const status = error instanceof ApiError ? error.status : undefined;
  const serverMessage = error instanceof Error ? error.message : undefined;

  if (status === 401) {
    return {
      title: 'You have been signed out',
      message: 'Sign in again to continue.',
      retryable: false,
    };
  }

  if (status === 403) {
    return {
      title: 'You do not have access',
      message: `Your account does not have permission to ${action}. Ask an administrator to grant you access.`,
      retryable: false,
    };
  }

  if (status === 404) {
    return {
      title: 'Nothing to show',
      message: "We couldn't find what you were looking for. It may have been moved or removed.",
      retryable: false,
    };
  }

  if (status === 408 || status === 504) {
    return {
      title: 'That took too long',
      message: 'The server did not respond in time. Try again in a moment.',
      retryable: true,
    };
  }

  if (status === 429) {
    return {
      title: 'Too many requests',
      message: 'Slow down for a moment, then try again.',
      retryable: true,
    };
  }

  if (status !== undefined && status >= 500) {
    return {
      title: 'Something went wrong',
      message: 'The server ran into a problem. Try again in a moment.',
      retryable: true,
    };
  }

  // 4xx we have no specific wording for: the server's own message is usually
  // the useful part (validation failures, conflicts), so use it when readable.
  if (status !== undefined && status >= 400) {
    return {
      title: 'That did not work',
      message: isPresentable(serverMessage)
        ? serverMessage
        : 'The server rejected the request. Check the details and try again.',
      retryable: false,
    };
  }

  // No status at all — a network fault, or a token we never managed to acquire.
  return {
    title: 'Unable to connect',
    message: "We couldn't reach the server. Check your connection and try again.",
    retryable: true,
  };
}

/** Single-line variant, for alerts and inline row errors. */
export function friendlyErrorMessage(error: unknown, options: DescribeOptions = {}): string {
  const { title, message } = describeApiError(error, options);
  return `${title}. ${message}`;
}
